import { z } from "zod";
import { bookTemplates } from "./catalog.js";
import { ulidSchema } from "./schemas.js";
import { studioContentProductionV1Schema } from "./studio.js";
/**
 * The memories a guided memoir asks for first, by prompt position in its template; every other
 * prompt is an optional extra. A book still keeps one chapter per prompt.
 */
export const guidedMemoirCorePromptPositions = {
    // A life arc: home, parents, youth, leaving home, love, work, family and what to pass on.
    story: [1, 2, 6, 8, 9, 13, 14, 17],
    // Our Story, told by one of the two (owner decision D4, gift mode): how they met, the first
    // date, the moment they knew, the day they committed, the first home, their ritual, what they
    // survived, and what they still want to do together.
    love: [1, 3, 4, 6, 7, 8, 10, 15],
};
/**
 * Questions asked inside a core memory, by template position, so its chapter grows deeper rather
 * than the book gaining thin chapters (2026-10-18 audit, A1): "What's your very first memory?" is
 * asked with the house the storyteller grew up in. The rest of the extras stay chapters of their
 * own.
 */
export const guidedMemoirMoreToTell = {
    story: { 1: [0, 3], 6: [4, 5], 13: [7], 14: [15], 17: [16] },
    love: { 1: [0, 2], 6: [5], 8: [9], 10: [11], 15: [12] },
};
/**
 * The memory a memoir closes on, set as the storyteller's letter (2026-10-18 audit, A3): to the
 * family in My Story ("What do you want the grandkids to know about you?"), to the partner in Our
 * Story ("What do you still want to do together?").
 */
export const guidedMemoirLetterPosition = { story: 17, love: 15 };
/**
 * The question asked before the memories, printed as the book's "Before you begin" in the
 * storyteller's own words (2026-10-18 audit, A3). It is never a chapter. Books made before it was
 * asked don't have it.
 */
export const guidedMemoirProloguePosition = { story: 18, love: 16 };
/** A memory worth writing a chapter from: a few sentences, not a fragment. */
export const GUIDED_MEMOIR_MINIMUM_WORDS = 25;
export function guidedMemoirWordCount(text) {
    return text.match(/[\p{L}\p{N}’'-]+/gu)?.length ?? 0;
}
export function isGuidedMemoirTemplate(templateId) {
    return Object.hasOwn(guidedMemoirCorePromptPositions, templateId);
}
/**
 * The template's prompts: the core memories, the optional extras in book order, and the question
 * asked before them. An extra asked inside a core memory names it (`within`).
 */
export function guidedMemoirPrompts(templateId) {
    const template = bookTemplates.find(({ id }) => id === templateId);
    if (!template)
        throw new Error(`Unknown memoir template: ${templateId}`);
    const core = new Set(guidedMemoirCorePromptPositions[templateId]);
    const prologuePosition = guidedMemoirProloguePosition[templateId];
    const within = new Map();
    for (const [corePosition, related] of Object.entries(guidedMemoirMoreToTell[templateId]))
        for (const position of related)
            within.set(position, Number(corePosition));
    const prompts = template.prompts.map((question, position) => ({
        position,
        question,
        core: core.has(position),
        ...(within.has(position) ? { within: within.get(position) } : {}),
    }));
    const prologue = prompts.find(({ position }) => position === prologuePosition);
    return {
        core: prompts.filter((prompt) => prompt.core),
        extras: prompts.filter((prompt) => !prompt.core && prompt.position !== prologuePosition),
        prologue: prologue ? { position: prologue.position, question: prologue.question } : null,
    };
}
const memoirPlanChapterSchema = z
    .object({
    memoryPosition: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(80),
    era: z.string().trim().max(60),
    place: z.string().trim().max(80),
    purpose: z.string().trim().min(1).max(300),
    /** The part of the book the chapter opens or continues ("Growing up"), for a longer book. */
    part: z.string().trim().min(1).max(40).optional(),
})
    .strict();
/** A proposed memoir plan: one chapter per memory the storyteller has told. */
export const memoirPlanV1Schema = z
    .object({
    title: z.string().trim().min(1).max(160),
    promise: z.string().trim().min(1).max(600),
    chapters: z.array(memoirPlanChapterSchema).min(1).max(40),
})
    .strict();
/** The buyer's approval: the plan's chapters in their chosen order, with their own titles. */
export const approveMemoirPlanCommandSchema = z
    .object({
    version: z.literal(1),
    artifactId: ulidSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    chapters: z
        .array(z
        .object({
        memoryPosition: z.number().int().nonnegative(),
        title: z.string().trim().min(1).max(80),
        /** The part as the family named it; empty takes the book's parts away. */
        part: z.string().trim().max(40).optional(),
    })
        .strict())
        .min(1)
        .max(40),
    /** The book's promise as the family worded it: the back cover's words. */
    promise: z.string().trim().min(1).max(600).optional(),
})
    .strict();
export const approvedMemoirPlanV1Schema = z
    .object({
    version: z.literal(1),
    artifactId: ulidSchema,
    /** The whole book's memories when the plan was made. */
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    /**
     * What the approval stands on: the planned memories and the storyteller details. It holds while
     * these are unchanged, whatever else happens in the book.
     */
    plannedSourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    title: z.string().trim().min(1).max(160),
    promise: z.string().trim().min(1).max(600),
    chapters: z
        .array(memoirPlanChapterSchema.extend({
        chapterId: z.string().min(1).max(160),
        /** The closing memory, set as the storyteller's letter (approved since 2026-10-18). */
        form: z.literal("LETTER").optional(),
    }))
        .min(1)
        .max(40),
    approvedBy: ulidSchema,
    approvedAt: z.string().datetime(),
})
    .strict();
/**
 * An approval that no longer stands, and why (2026-11-02, E9 and F4): a planned memory or the
 * storyteller details changed after it was given, or the family reopened the book to make changes.
 * Either way the plan is approved again before the book goes on.
 */
export const memoirPlanSetAsideV1Schema = z
    .object({
    reason: z.enum(["MEMORIES_CHANGED", "REOPENED"]),
    approvedAt: z.string().datetime(),
    reopenedAt: z.string().datetime().optional(),
})
    .strict();
/** Where a guided memoir's chapter plan stands. */
export const memoirPlanStateV1Schema = z
    .object({
    /** The book revision a plan request or approval must name. */
    bookRevision: z.number().int().nonnegative(),
    production: studioContentProductionV1Schema,
    missingCorePositions: z.array(z.number().int().nonnegative()),
    approved: approvedMemoirPlanV1Schema.nullable(),
    /** The last approval, when it no longer stands; absent from servers before 2026-11-02. */
    setAside: memoirPlanSetAsideV1Schema.nullable().optional(),
})
    .strict();
//# sourceMappingURL=guided-memoir.js.map